import { useState } from "react";
import { useClerk } from "@clerk/react";
import { Download, Trash2, ShieldCheck, Loader2, FileText } from "lucide-react";
import { useGetMe } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function RgpdCard() {
  const { toast } = useToast();
  const { signOut } = useClerk();
  const { data: me } = useGetMe();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const accountEmail = me?.email ?? "";

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${basePath}/api/me/export`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Échec de l'export (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `onenext-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({
        title: "Export téléchargé",
        description:
          "Vos données ont été exportées au format JSON (Article 20 du RGPD).",
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error
            ? err.message
            : "Impossible d'exporter vos données pour le moment.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (deleting) return;
    if (!accountEmail) {
      toast({
        title: "Adresse e-mail introuvable",
        description: "Rechargez la page et réessayez.",
        variant: "destructive",
      });
      return;
    }
    if (confirmText.trim().toLowerCase() !== accountEmail.toLowerCase()) {
      toast({
        title: "Confirmation invalide",
        description:
          "Saisissez exactement votre adresse e-mail pour confirmer.",
        variant: "destructive",
      });
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch(`${basePath}/api/me`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmEmail: confirmText.trim() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `Échec de la suppression (HTTP ${res.status})`);
      }
      toast({
        title: "Compte supprimé",
        description: "Toutes vos données ont été effacées.",
      });
      await signOut({ redirectUrl: `${basePath || ""}/` });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error
            ? err.message
            : "Impossible de supprimer le compte pour le moment.",
        variant: "destructive",
      });
      setDeleting(false);
    }
  }

  return (
    <Card className="rounded-2xl border-destructive/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" /> Mes données (RGPD)
        </CardTitle>
        <CardDescription>
          Exercez vos droits de portabilité (Article 20) et d'effacement
          (Article 17). Pour les autres droits, utilisez le{" "}
          <Link href="/contact" className="text-primary hover:underline">
            formulaire de contact
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-medium">Exporter mes données</p>
          <p className="text-sm text-muted-foreground">
            Téléchargez un fichier JSON contenant l'ensemble de vos données :
            compte, tâches, projets, notes, e-mails synchronisés, historiques
            d'IA et de facturation.
          </p>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="rgpd-export"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger mes données (JSON)
          </Button>
        </div>

        <div className="space-y-2 pt-4 border-t">
          <p className="text-sm font-medium">Documents juridiques</p>
          <p className="text-sm text-muted-foreground">
            Téléchargez l'accord de traitement des données (DPA) que vous
            pouvez archiver pour vos propres dossiers de conformité.
          </p>
          <a
            href={`${basePath}/api/legal/dpa.md`}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-accent transition-colors"
            data-testid="rgpd-dpa-download"
          >
            <FileText className="w-4 h-4" />
            Télécharger le DPA (Markdown)
          </a>
        </div>

        <div className="space-y-2 pt-4 border-t">
          <p className="text-sm font-medium text-destructive">
            Supprimer mon compte
          </p>
          <p className="text-sm text-muted-foreground">
            Cette action est <strong>définitive et irréversible</strong>.
            Toutes vos données (tâches, projets, e-mails synchronisés,
            historique IA, facturation) seront immédiatement effacées et
            votre identité Clerk sera supprimée. Les éventuelles demandes de
            support que vous avez envoyées seront anonymisées.
          </p>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmText("");
              setDeleteOpen(true);
            }}
            data-testid="rgpd-delete-open"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer définitivement mon compte
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement mon compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Pour confirmer, saisissez
              ci-dessous votre adresse e-mail{" "}
              <strong>{accountEmail || "(inconnue)"}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rgpd-confirm">Confirmation par e-mail</Label>
            <Input
              id="rgpd-confirm"
              type="email"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={accountEmail}
              autoComplete="off"
              data-testid="rgpd-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={
                deleting ||
                !accountEmail ||
                confirmText.trim().toLowerCase() !== accountEmail.toLowerCase()
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="rgpd-confirm-delete"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression…
                </>
              ) : (
                <>Supprimer définitivement</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
